"use client";

import Link from "next/link";
import { createContext, useContext } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@velpha/ui/components/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@velpha/ui/components/hover-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@velpha/ui/components/table";
import { cn } from "@velpha/ui/lib/utils";
import { docIcons, type DocIconName } from "@/lib/doc-icons";
import { getTranslations } from "@/lib/i18n";
import { getDocTerm, type DocTagTone, type DocTermId } from "@/lib/doc-terms";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { Locale } from "@/lib/i18n";

// ── Locale context ──────────────────────────────────────────────────

const DocLocaleContext = createContext<Locale>("ja");
export const DocLocaleProvider = DocLocaleContext.Provider;
export function useDocLocale(): Locale {
  return useContext(DocLocaleContext);
}

const DOC_TAG_META: Record<DocTagTone, string> = {
  model: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  metric: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  state: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400",
  risk: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400",
  ui: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  data: "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300",
};

function IconBadge({ icon: Icon, className }: { icon?: LucideIcon; className?: string }) {
  if (!Icon) return null;
  return (
    <div className={cn("velpha-icon-badge", className)}>
      <Icon className="size-[18px]" />
    </div>
  );
}

export function DocIcon({ name, className }: { name: DocIconName; className?: string }) {
  const Icon = docIcons[name];
  return <Icon className={className} />;
}

export function DocCardGrid({ children, columns = 2 }: { children: ReactNode; columns?: 2 | 3 }) {
  return <div className={cn("my-6 grid gap-4", columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2")}>{children}</div>;
}

export function DocCard({
  icon,
  title,
  description,
  href,
  children,
}: {
  icon: DocIconName;
  title: string;
  description?: string;
  href?: string;
  children?: ReactNode;
}) {
  const locale = useDocLocale();
  const Icon = docIcons[icon];
  const resolvedHref = href?.startsWith("/") ? `/${locale}${href}` : href;
  const card = (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <IconBadge icon={Icon} />
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-base">{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </div>
        </div>
      </CardHeader>
      {children ? <CardContent>{children}</CardContent> : null}
    </Card>
  );

  if (!resolvedHref) return card;

  return (
    <Link href={resolvedHref} className="block h-full no-underline">
      {card}
    </Link>
  );
}

export function DocCallout({ icon, title, children }: { icon: DocIconName; title: string; children: ReactNode }) {
  const Icon = docIcons[icon];

  return (
    <div className="velpha-doc-callout my-6 rounded-2xl border border-fd-secondary-foreground/15 bg-fd-secondary/55 px-5 py-4">
      <div className="flex items-start gap-3">
        <IconBadge icon={Icon} className="size-9" />
        <div className="min-w-0">
          <div className="text-sm font-semibold tracking-tight">{title}</div>
          <div className="mt-2 text-sm text-muted-foreground">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function DocSummary({ title, children }: { title?: string; children: ReactNode }) {
  const locale = useDocLocale();
  const t = getTranslations(locale);
  const resolvedTitle = title ?? t.summaryTitle;

  return (
    <div className="my-6 rounded-2xl border border-fd-primary/15 bg-fd-primary/5 px-5 py-4">
      <div className="text-sm font-semibold tracking-tight">{resolvedTitle}</div>
      <div className="mt-3 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

export function DocTag({ children, tone = "metric" }: { children: ReactNode; tone?: DocTagTone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide",
        DOC_TAG_META[tone]
      )}
    >
      {children}
    </span>
  );
}

export function DocTerm({
  termKey,
  locale: localeProp,
  showTone = false,
  children,
}: {
  termKey: DocTermId;
  locale?: Locale;
  showTone?: boolean;
  children?: ReactNode;
}) {
  const contextLocale = useDocLocale();
  const locale = localeProp ?? contextLocale;
  const definition = getDocTerm(termKey, locale);
  const resolvedLabel = children ?? definition.label;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-1 align-baseline text-left font-medium text-foreground underline decoration-dotted underline-offset-3 hover:bg-fd-primary/5"
        >
          <span>{resolvedLabel}</span>
          {showTone ? <DocTag tone={definition.tone}>{definition.tag}</DocTag> : null}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="font-medium">{definition.label}</div>
            {showTone ? <DocTag tone={definition.tone}>{definition.tag}</DocTag> : null}
          </div>
          <div className="text-sm leading-relaxed text-muted-foreground">{definition.summary}</div>
        </div>
        {definition.detail ? (
          <div className="mt-2 text-sm leading-relaxed text-muted-foreground">{definition.detail}</div>
        ) : null}
      </HoverCardContent>
    </HoverCard>
  );
}

export function DocTable({
  title,
  columns,
  rows,
  note,
  plain = false,
}: {
  title: ReactNode;
  columns: string[];
  rows: Array<Array<ReactNode>>;
  note?: ReactNode;
  plain?: boolean;
}) {
  const content = (
    <>
      <div className="text-base font-semibold tracking-tight">{title}</div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${title}-${index}`}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={`${index}-${cellIndex}`} className={cn("align-top text-sm")}>
                    <span className="whitespace-normal leading-4">{cell}</span>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {note ? <div className="mt-4 text-sm text-muted-foreground">{note}</div> : null}
    </>
  );

  if (plain) return <div className="my-6">{content}</div>;

  return <div className="">{content}</div>;
}
